import type { FC } from 'react';
import { lazy } from 'react';
import { useMemoOne } from 'use-memo-one';
import type {
  TransactionType,
  TransactionsParams,
} from 'src/apis/capacity-management/types';
import { useCapacityCategories } from 'src/apis/capacity-management/use-capacity-categories';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useMarketAreas } from 'src/apis/capacity-management/use-market-areas';
import { useMyTransactions } from 'src/apis/capacity-management/use-my-transactions';
import { useReshuffleAllocationActions } from 'src/apis/reshuffle-service/use-reshuffle-allocation-actions';
import { useOptionalReshuffleServiceRoot } from 'src/apis/reshuffle-service/use-reshuffle-service';
import { useCreateSurrenderPaths } from 'src/apis/surrender-service/use-create-surrender-paths';
import { useOptionalSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { PageHeader } from 'src/components/layout/page-header';
import { PageSpinner } from 'src/components/spinner-container';
import { useMyTransactionsPageParams } from 'src/pages/reporting/my-transactions/use-page-params';

const MyTransactionsPage = lazy(() => import('./page'));

export const ReportingTransactions: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const surrenderRoot = useOptionalSurrenderRoot();
  const capacityCategories = useCapacityCategories({ capacityManagementRoot });
  const marketAreas = useMarketAreas({ capacityManagementRoot });
  const reshuffleServiceRoot = useOptionalReshuffleServiceRoot();

  const pageParams = useMyTransactionsPageParams();

  const isReshufflingEnabled = Boolean(
    reshuffleServiceRoot?.response?.data._links?.searchAllocations
  );

  const params = useMemoOne<TransactionsParams>(() => {
    const {
      start,
      pageSize,
      networkPointId,
      tsoId,
      transactionTypeSpecificId,
      transactionType,
      ...rest
    } = pageParams.value;

    // merging 'DIRECT_FCFS', 'FCFS' into one filter
    const processedTransactionType: TransactionType[] = transactionType.flatMap(
      (type) =>
        type === 'DIRECT_FCFS' || type === 'FCFS'
          ? ['DIRECT_FCFS', 'FCFS']
          : [type]
    );

    return {
      offset: start,
      limit: pageSize,
      networkPointId: networkPointId.map((id) => id.value),
      tsoId: tsoId.map((id) => id.value),
      transactionType: processedTransactionType,
      transactionTypeSpecificId: transactionTypeSpecificId
        ? [transactionTypeSpecificId]
        : [],
      ...rest,
    };
  }, [pageParams.value]);

  const myTransactions = useMyTransactions({
    capacityManagementRoot,
    params,
  });

  const getCreateSurrenderPath = useCreateSurrenderPaths(
    myTransactions,
    surrenderRoot
  );

  const reshuffleActions = useReshuffleAllocationActions(
    myTransactions,
    reshuffleServiceRoot
  );

  if (
    !capacityManagementRoot.response ||
    !capacityCategories.response ||
    !marketAreas.response ||
    !surrenderRoot.loaded ||
    !myTransactions.response
  )
    return <PageSpinner />;

  const hasMultiYearTransactions =
    capacityManagementRoot.response.data._links
      ?.multiYearTransactionTransportAllocation?.href;

  return (
    <div className="grid items-center gap-xl">
      <PageHeader title="My Transactions" data-testid="title-my-transactions" />
      <MyTransactionsPage
        myTransactions={myTransactions}
        pageParams={pageParams}
        capacityCategories={capacityCategories}
        marketAreas={marketAreas}
        getCreateSurrenderPath={getCreateSurrenderPath}
        isSurrenderForAllEnabled={
          !!surrenderRoot.response?.data._links.isSurrenderForAllEnabled
        }
        reshuffleActions={isReshufflingEnabled ? reshuffleActions : undefined}
        hasMultiYearTransactions={Boolean(hasMultiYearTransactions)}
      />
    </div>
  );
};
